// Google Ads / campaign attribution tracking.
// Parses the URL params from the campaign tracking template, persists them for
// the whole funnel, and manages a stable session id used to de-dupe clicks and
// link a click to the lead it produced.

const SESSION_KEY = 'osgd_session_id';
const TRACK_KEY = 'osgd_tracking';
const PII_KEY = 'lp_pii';
const FBP_COOKIE = '_fbp';
const FBC_COOKIE = '_fbc';
const FBCLID_KEY = 'lp_fbclid';

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'sess-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// Shared cross-subdomain cookies (readable by both apply.thelemonpros.com
// and the static thelemonpros.com Astro site via Domain=.thelemonpros.com).
const SHARED_ATTR_COOKIE = 'lp_attr';
const SHARED_SID_COOKIE = 'lp_sid';
function rootDomain() {
  try {
    const h = window.location.hostname || '';
    if (h === 'thelemonpros.com' || h.endsWith('.thelemonpros.com')) return '.thelemonpros.com';
  } catch (e) { /* ignore */ }
  return '';
}

// ─── Meta Pixel helpers ─────────────────────────────────────────────────
function setCookie(name, value, days = 90) {
  const maxAge = days * 24 * 60 * 60;
  const dom = rootDomain();
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax; Secure${dom ? `; Domain=${dom}` : ''}`;
}
function getCookie(name) {
  const match = document.cookie
    .split('; ')
    .find((c) => c.startsWith(name + '='));
  return match ? decodeURIComponent(match.split('=')[1] || '') : '';
}

/** Ensure a _fbp browser cookie exists (Meta's first-party cookie for
 * anonymous browsers). Mirrors what the Pixel script would set on first hit. */
export function ensureFbp() {
  let fbp = getCookie(FBP_COOKIE);
  if (!fbp) {
    fbp = `fb.1.${Date.now()}.${Math.floor(Math.random() * 1e10)}`;
    setCookie(FBP_COOKIE, fbp, 90);
  }
  return fbp;
}

/** Capture &fbclid= from the URL and store it as the _fbc cookie so future
 * server CAPI events include it (dramatically boosts Meta match quality). */
export function captureFbclid(search) {
  const p = new URLSearchParams(search || (typeof window !== 'undefined' ? window.location.search : ''));
  const fbclid = p.get('fbclid');
  if (fbclid) {
    const fbc = `fb.1.${Date.now()}.${fbclid}`;
    setCookie(FBC_COOKIE, fbc, 90);
    try { localStorage.setItem(FBCLID_KEY, fbclid); } catch (e) { /* ignore */ }
    return fbc;
  }
  return getCookie(FBC_COOKIE) || '';
}

export function getFbc() { return getCookie(FBC_COOKIE) || ''; }
export function getFbp() { return getCookie(FBP_COOKIE) || ''; }

/** True when the current visitor's session shows Meta/Facebook attribution.
 * Signals (in priority order):
 *   1. `fbclid` in the current URL — freshest signal
 *   2. `_fbc` cookie present (captured on an earlier hit during this browser session)
 *   3. `lp_fbclid` localStorage key (captured on entry, persists across the funnel)
 * Used by landing pages to swap the display phone number to the dedicated
 * Meta tracking line, and by call-click logging for attribution. */
export function isMetaVisitor() {
  if (typeof window === 'undefined') return false;
  try {
    const p = new URLSearchParams(window.location.search || '');
    if (p.get('fbclid')) return true;
    if (getCookie(FBC_COOKIE)) return true;
    if (localStorage.getItem(FBCLID_KEY)) return true;
  } catch (e) { /* ignore */ }
  return false;
}

/** Unique event id used to dedupe browser Pixel + server CAPI. */
export function makeFbEventId(prefix = 'evt') {
  return `${prefix}-${uuid()}`;
}

/**
 * PII the visitor has voluntarily typed into our funnel. Persisted to
 * localStorage so every subsequent PageView / ViewContent / Contact CAPI
 * fire inherits it — dramatically boosts Meta's Event Match Quality
 * (has_pii % on the pixel dashboard).
 *
 * Only fields typed by the user go here — we NEVER auto-populate from
 * external sources. Cleared automatically on ThankYou (session done).
 */
const PII_FIELDS = ['email', 'phone', 'first_name', 'last_name', 'city', 'state', 'zip_code'];

export function savePii(patch = {}) {
  try {
    const current = readPii();
    const next = { ...current };
    PII_FIELDS.forEach((k) => {
      const v = patch[k];
      if (v && String(v).trim()) next[k] = String(v).trim();
    });
    // Meta forms commonly send full_name as one string — split it.
    if (patch.full_name && !patch.first_name && !patch.last_name) {
      const parts = String(patch.full_name).trim().split(/\s+/);
      if (parts.length) next.first_name = parts[0];
      if (parts.length > 1) next.last_name = parts.slice(1).join(' ');
    }
    localStorage.setItem(PII_KEY, JSON.stringify(next));
    return next;
  } catch (e) { return {}; }
}

export function readPii() {
  try { return JSON.parse(localStorage.getItem(PII_KEY) || '{}') || {}; }
  catch (e) { return {}; }
}

export function clearPii() {
  try { localStorage.removeItem(PII_KEY); } catch (e) { /* ignore */ }
}

/** Load the Meta Pixel base script exactly once. Safe to call on every route.
 *  Fires the initial PageView with a fresh event_id AND mirrors it via server
 *  CAPI in parallel — Meta dedupes on event_id, so we get 2 delivery paths for
 *  every event (~30% attribution lift vs. browser-only tracking). */
let _pixelLoaded = false;
export function loadMetaPixel(pixelId) {
  if (_pixelLoaded || !pixelId) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.fbq) { _pixelLoaded = true; return; }
  _pixelLoaded = true;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];
    t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s);
  }(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', pixelId);
  ensureFbp();
  // Fire the initial PageView through the dual-delivery helper so it lands
  // as one deduped event on Meta's side (browser + server).
  trackMetaCapi('PageView');
}

/** Track any Meta event by name; passes the eventID so server CAPI can dedupe. */
export function trackMeta(eventName, params = {}, eventId = '') {
  try {
    if (typeof window === 'undefined' || !window.fbq) return;
    if (eventId) {
      window.fbq('track', eventName, params, { eventID: eventId });
    } else {
      window.fbq('track', eventName, params);
    }
  } catch (e) { /* never block */ }
}

/**
 * Fire a Meta Pixel event through BOTH the browser Pixel AND our server CAPI,
 * sharing the same event_id so Meta dedupes them on their side.
 *
 * Optional `pii` fields (email, phone, first_name, ...) are only used server-
 * side — the browser Pixel picks them up from Automatic Advanced Matching
 * (already enabled on the pixel).
 *
 *   trackMetaCapi('PageView');
 *   trackMetaCapi('Contact', { phone_tapped: '8443358911' });
 *   trackMetaCapi('Lead', {}, { email, phone, first_name, last_name });
 */
export function trackMetaCapi(eventName, customData = {}, pii = {}) {
  if (typeof window === 'undefined') return '';
  const eventId = makeFbEventId(eventName.toLowerCase());
  // 1) Browser Pixel (client-side)
  trackMeta(eventName, customData, eventId);
  // 2) Server CAPI mirror — fire-and-forget. If the backend URL isn't
  // configured (dev preview) or fetch fails, we silently no-op.
  try {
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return eventId;
    // Merge stored PII (what the visitor typed into the funnel so far)
    // with any explicit PII passed by the caller. Explicit wins.
    const stored = readPii();
    const merged = { ...stored, ...pii };
    // Also try to snapshot the phone/email typed live in visible <input>
    // fields (Meta's Advanced Matching does this for the browser Pixel,
    // but we need it on server side too). Cheap DOM read, no framework.
    if (!merged.phone) {
      const el = document.querySelector('input[type="tel"], input[name*="phone" i]');
      const v = (el && el.value) ? String(el.value).replace(/\D/g, '') : '';
      if (v && v.length >= 10) merged.phone = v;
    }
    if (!merged.email) {
      const el = document.querySelector('input[type="email"], input[name*="email" i]');
      const v = (el && el.value) ? String(el.value).trim() : '';
      if (v && /@/.test(v)) merged.email = v;
    }
    const body = {
      event_name: eventName,
      event_id: eventId,
      event_source_url: window.location.href,
      fbp: getFbp(),
      fbc: getFbc(),
      external_id: getSessionId(),
      email: merged.email || '',
      phone: merged.phone || '',
      first_name: merged.first_name || '',
      last_name: merged.last_name || '',
      city: merged.city || '',
      state: merged.state || '',
      zip_code: merged.zip_code || merged.zip || '',
      custom_data: customData && Object.keys(customData).length ? customData : null,
    };
    // Use fetch with keepalive so the request survives navigation (Contact
    // events fire during a tel: tap). sendBeacon can't set JSON headers.
    fetch(`${backend}/api/track/capi`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
  } catch (e) { /* never block */ }
  return eventId;
}


export function getSessionId() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    // A visitor who first browsed thelemonpros.com already has a shared
    // session cookie — adopt it so both sites share one journey.
    s = getCookie(SHARED_SID_COOKIE) || uuid();
    localStorage.setItem(SESSION_KEY, s);
  }
  try { setCookie(SHARED_SID_COOKIE, s); } catch (e) { /* ignore */ }
  return s;
}

// Mirror the attribution snapshot into the shared root-domain cookie so the
// static thelemonpros.com site can attach it to its own forms/calls.
function mirrorSharedAttr(data) {
  try {
    const compact = {};
    Object.keys(data).forEach((k) => {
      if (k === 'params' || k === 'referrer') return;
      if (data[k]) compact[k] = data[k];
    });
    if (Object.keys(compact).length) {
      setCookie(SHARED_ATTR_COOKIE, JSON.stringify(compact));
    }
  } catch (e) { /* ignore */ }
}

function readSharedAttr() {
  try {
    const raw = getCookie(SHARED_ATTR_COOKIE);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

// Extra params we store but don't use as primary dimensions.
// `tpl` + `tpl_lang` are stamped by email CTA links (template helper in
// backend/email_templates/_shared.py) and are REQUIRED for backend
// `/leads` to tag the lead as Source="Email Marketing" in Quickbase.
// `utm_campaign` is forwarded verbatim too so the backend's fallback
// `_utm_camp` branch resolves the template key when `tpl` is absent.
const PASSTHROUGH = [
  'feeditemid', 'targetid', 'loc_interest_ms', 'loc_physical_ms', 'matchtype',
  'network', 'device', 'devicemodel', 'placement', 'adposition', 'target',
  'tpl', 'tpl_lang', 'utm_campaign', 'utm_medium',
];

// Google Ads ValueTrack suffix appended to every landing-page link copied from
// the admin, so campaign/ad-group/keyword/creative attribution is captured.
// tg_ref=campaign, adgroup_id, keyword, sub2=creative(ad), + passthrough params.
export const AD_TRACKING_QS = 'tg_ref={campaignid}&adgroup_id={adgroupid}&keyword={keyword}&sub2={creative}&feeditemid={feeditemid}&targetid={targetid}&loc_interest_ms={loc_interest_ms}&loc_physical_ms={loc_physical_ms}&matchtype={matchtype}&network={network}&device={device}&devicemodel={devicemodel}&placement={placement}&adposition={adposition}&target={target}';

// Append the tracking suffix to a base URL, e.g. https://x.com/split2/?tg_ref=...
export const withAdTracking = (base) => `${String(base).replace(/\/+$/, '')}/?${AD_TRACKING_QS}`;

// Meta / Facebook Ads URL parameter template. Meta uses {{...}} macro syntax
// that ads manager auto-substitutes at delivery. utm_source=facebook ensures our
// backend tags the lead's `network` field as Facebook (Instagram inherits when
// placement contains "instagram"). fbclid is auto-appended by Meta on click.
export const FB_TRACKING_QS = 'utm_source=facebook&utm_medium=paid_social&utm_campaign={{campaign.name}}&utm_content={{ad.name}}&utm_term={{adset.name}}&campaign_id={{campaign.id}}&adgroup_id={{adset.id}}&ad_id={{ad.id}}&placement={{placement}}';

// Append the Meta tracking suffix to a base URL.
export const withFbTracking = (base) => `${String(base).replace(/\/+$/, '')}/?${FB_TRACKING_QS}`;

// OpenAI / ChatGPT Ads URL parameter template. OpenAI Ads Manager auto-fills
// {campaign_id}, {campaign_name}, {ad_id}, {ad_name} macros at delivery.
// utm_source=openai is what triggers our backend's OpenAI attribution branch
// (lead's `network` → 'openai' → QB Source "Chat GPT Ads Forms", card in
// Channels tab, Conversions API `lead_created` fires server-side).
// OpenAI Ads tracking template — pasted into Pages → "OpenAI / ChatGPT Ads
// Final URL" and used verbatim in platform.openai.com/ads campaign setup.
// `oppref` is reserved by OpenAI (auto-appended at click time); `{campaign_name}`
// and `{ad_name}` aren't supported by OpenAI — only the ID placeholders are,
// so we drop them to avoid literal "{campaign_name}" strings leaking into
// landing-page URLs.
export const OAI_TRACKING_QS = 'utm_source=openai&utm_medium=cpc&campaign_id={campaign_id}&adgroup_id={ad_group_id}&ad_id={ad_id}';

// Append the OpenAI tracking suffix to a base URL.
export const withOpenAITracking = (base) => `${String(base).replace(/\/+$/, '')}/?${OAI_TRACKING_QS}`;


const EMPTY = { campaign_id: '', adgroup_id: '', ad_id: '', keyword: '', gclid: '', gbraid: '', wbraid: '', referrer: '', feeditemid: '', extensionid: '', split_experiment_id: '', split_variant: '', campaign_name: '', adgroup_name: '', ad_name: '', utm_source: '', placement: '', params: {} };

// Map the current landing-page path -> its internal source_page code, so a phone
// tap is attributed to the exact page even when the number is shared across pages.
const PATH_TO_SOURCE = {
  '/': 'home', '/sp': 'sp', '/pa': 'lapa', '/spa': 'laspa',
  '/tm': 'latm', '/tm2': 'latm2', '/dg': 'ladg', '/dgs': 'ladgs',
};
function currentSourcePage() {
  try {
    const path = (window.location.pathname || '/').replace(/\/+$/, '') || '/';
    return PATH_TO_SOURCE[path] || '';
  } catch (e) { return ''; }
}

// Log a phone-number TAP (source_page + dialed number) so the resulting inbound
// call can be tied back to the exact landing page. Uses sendBeacon so it fires
// even as the tel: navigation begins. telHref example: "tel:+18443358911".
// ALSO fires a Meta 'Contact' event via browser Pixel + server CAPI so Meta
// counts the tap as a conversion signal (Advantage+ audience relies on this).
export function recordCallClick(telHref) {
  try {
    const number = String(telHref || '').replace(/\D/g, '').slice(-10);
    if (!number) return;
    const t = getTracking();
    const body = JSON.stringify({
      session_id: getSessionId(),
      source_page: currentSourcePage(),
      number,
      gclid: t.gclid || '',
      campaign_id: t.campaign_id || '',
      adgroup_id: t.adgroup_id || '',
      ad_id: t.ad_id || '',
      keyword: t.keyword || '',
    });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/track/call-click`;
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true });
    }
    // Meta Contact event — dual-fire browser + server so the tap counts even
    // on iOS/Safari where the browser Pixel is often blocked.
    trackMetaCapi('Contact', {
      content_name: 'phone_tap',
      content_category: currentSourcePage() || 'landing_page',
      phone_tapped: number,
    });
  } catch (e) { /* never block the call */ }
}

export function getTracking() {
  try {
    const raw = localStorage.getItem(TRACK_KEY);
    if (raw) return { ...EMPTY, ...JSON.parse(raw) };
    // No local attribution — fall back to the shared cookie (attribution
    // captured on thelemonpros.com carries over to this funnel app).
    const shared = readSharedAttr();
    if (shared) return { ...EMPTY, ...shared };
    return { ...EMPTY };
  } catch (e) {
    return { ...EMPTY };
  }
}

// Read tracking params from the current URL. If present, persist & return them.
// Otherwise return whatever was previously stored (so attribution survives the
// funnel and refreshes without losing the original source).
export function captureTracking(search) {
  const p = new URLSearchParams(search || '');
  const existing = getTracking();
  // Google Ads uses `tg_ref` (campaign) / `sub2` (creative/ad) via ValueTrack;
  // Meta's URL template uses literal `campaign_id` / `ad_id` + utm_* macros
  // that Ads Manager substitutes at click time. Accept both so a Meta click
  // populates the same fields a Google click does.
  const utmSourceRaw = (p.get('utm_source') || '').toLowerCase();
  const isMetaSrc = ['facebook', 'instagram', 'fb', 'ig'].includes(utmSourceRaw);
  const data = {
    campaign_id: p.get('tg_ref') || p.get('campaign_id') || '',
    adgroup_id:  p.get('adgroup_id') || '',
    // `utm_term` on Meta = adset name (not a search keyword), so only borrow
    // it into `keyword` when this ISN'T Meta traffic.
    keyword:     p.get('keyword') || (isMetaSrc ? '' : (p.get('utm_term') || '')),
    ad_id:       p.get('sub2') || p.get('ad_id') || '',
    // Human-readable names — Meta passes these as utm_campaign / utm_content /
    // utm_term. Google's Ads never sends readable names, so these stay empty
    // for Google traffic (frontend + backend fall back to the ID label map).
    campaign_name: p.get('utm_campaign') || '',
    ad_name:       p.get('utm_content')  || '',
    adgroup_name:  p.get('utm_term')     || '',
    utm_source:    utmSourceRaw,
    placement:     (p.get('placement') || '').toLowerCase(),
    gclid: p.get('gclid') || '',
    gbraid: p.get('gbraid') || '',
    wbraid: p.get('wbraid') || '',
    referrer: (typeof document !== 'undefined' ? document.referrer : '') || '',
    feeditemid: p.get('feeditemid') || '',
    extensionid: p.get('extensionid') || '',
    // Split-test attribution (stamped by the /split entry redirect). Sticky across
    // the funnel so the lead carries the experiment + variant it was routed to.
    split_experiment_id: p.get('se') || existing.split_experiment_id || '',
    split_variant: p.get('sv') || existing.split_variant || '',
    params: {},
  };
  PASSTHROUGH.forEach((k) => {
    const v = p.get(k);
    if (v) data.params[k] = v;
  });

  const hasTracking =
    data.campaign_id || data.adgroup_id || data.ad_id || data.keyword ||
    data.campaign_name || data.adgroup_name || data.ad_name ||
    data.utm_source || data.placement ||
    data.gclid || data.gbraid || data.wbraid || data.referrer ||
    data.feeditemid || data.extensionid || p.get('se') ||
    Object.keys(data.params).length > 0;

  if (hasTracking) {
    try {
      localStorage.setItem(TRACK_KEY, JSON.stringify(data));
    } catch (e) {
      /* ignore */
    }
    mirrorSharedAttr(data);
    return data;
  }
  const stored = getTracking();
  mirrorSharedAttr(stored);
  return stored;
}
